<script setup>
import {ref, onMounted, computed, watch, watchEffect} from 'vue'
import FilterMenu from '@/components/common/Filters/FilterMenu.vue'
import ChartBar from "@/components/dashboard/widgets/ChartBar.vue";
import ChartArea from "@/components/dashboard/widgets/ChartArea.vue";
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import {fetchDashboardData} from "@/services/api/widgetData.js";
import Title from "@/components/dashboard/components/Title.vue";

const { t, locale } = useI18n()
const display = useDisplay()
const isMobile = computed(() => display.width.value <= 1264)
const dashboard = ref({});
const selectedPeriodKey = ref('toDay')

const filters = computed(() => [
    {
        key: 'toDay',
        label: t('common.times.toDay'),
    },
    {
        key: 'weekly',
        label: t('common.times.weekly'),
    },
    {
        key: 'monthly',
        label: t('common.times.monthly'),
    },
]);

const componentsMap = {
    ChartBar,
    ChartArea
}

const props = defineProps({
    jsonData: Object,
    url: String,
});

onMounted(async () => {
    dashboard.value = props.jsonData
    if(props.url) {
        dashboard.value = await fetchDashboardData(props.url)
    }

    const selectedWidget = dashboard.value.widgets.find(w => w.selected)
    if (selectedWidget) {
        animationSelectChart(selectedWidget.id)
    }
});

const dateRange = computed(() => {
    const today = new Date();
    let startDate;
    let params;

    switch (selectedPeriodKey.value) {
        case 'weekly':
            startDate = new Date(today);
            startDate.setDate(today.getDate() - 6);
            params = {
                series: {
                    start: format(startDate, 'yyyy-MM-dd') + 'T00:00:00Z-03:00',
                    end: format(today, 'yyyy-MM-dd') + 'T23:59:59Z-03:00',
                    interval: 'DAY'
                },
                aggregate: 'MAX'
            };
            break;

        case 'monthly':
            startDate = new Date(today);
            startDate.setDate(today.getDate() - 31);
            params = {
                series: {
                    start: format(startDate, 'yyyy-MM-dd') + 'T00:00:00Z-03:00',
                    end: format(today, 'yyyy-MM-dd') + 'T23:59:59Z-03:00',
                    interval: 'DAY'
                },
                aggregate: 'MAX'
            };
            break;

        case 'toDay':
        default:
            params = {
                start: format(today, 'yyyy-MM-dd') + 'T00:00:00-03:00',
                end: format(today, 'yyyy-MM-dd') + 'T23:59:59-03:00'
            };
            break;
    }

    return params;
});

function animationSelectChart(id) {
    dashboard.value.widgets.forEach(widget => {
        widget.selected = widget.id === id;
    });

    const index = dashboard.value.widgets.findIndex(w => w.id === id);
    const [selectedWidget] = dashboard.value.widgets.splice(index, 1);
    const targetIndex = 0;

    dashboard.value.widgets.splice(targetIndex, 0, selectedWidget);
}

</script>

<template>
    <div class="justify-content-end v-card-header">
        <div class="mx-7 mt-3 stats-list d-sm-flex align-items-center">
            <FilterMenu v-model="selectedPeriodKey" :listFilters="filters" />
        </div>
    </div>
    <v-row class="mx-5 mb-5">
        <transition-group v-if="!isMobile" name="chart" tag="div" class="d-flex flex-wrap w-100">
            <v-col
                v-for="widget in dashboard?.widgets"
                :key="widget.id"
                :lg="widget.selected ? 9 : 3"
                :cols="12"
                class="chart-transition"
            >
                <v-card @click="animationSelectChart(widget.id)" class="total-card border-radius hoverable">
                    <v-row class="custom-tooltip d-flex justify-content-end">
                        <i class="ri-information-line text-black"
                           v-tooltip="widget.i18n[locale.slice(0, 2)]?.information"></i>
                    </v-row>
                    <v-row class="align-center" dense>
                        <Title :data="widget.i18n" />
                    </v-row>
                    <component
                        :is="componentsMap[widget.type]"
                        :jsonData="widget"
                        :interval="dateRange"
                    />
                </v-card>
            </v-col>
        </transition-group>
        <v-col v-else
            v-for="widget in dashboard?.widgets"
            :key="widget.id"
            :lg="12"
            :cols="12"
            class="chart-transition"
        >
            <v-card class="total-card border-radius pa-4 hoverable">
                <v-row class="custom-tooltip d-flex justify-content-end">
                    <i class="ps-1 ri-information-line text-black"
                       v-tooltip="widget.i18n[locale.slice(0, 2)]?.information"></i>
                </v-row>
                <v-row class="align-center" dense>
                    <Title :data="widget.i18n" />
                </v-row>
                <component
                    :is="componentsMap[widget.type]"
                    :jsonData="widget"
                    :interval="dateRange"
                />
            </v-card>
        </v-col>
    </v-row>
</template>
<style>
.custom-tooltip i {
    position:absolute;
    top:15px;
    margin-right:14px;
    font-size: 18px;
}
.total-card {
    border: 1px solid var(--borderColor)!important;
    padding-left: 20px!important;
    padding-top: 48px!important;
    min-height: 310px;
}
.hoverable {
    cursor: pointer;
    transition: all 0.3s ease;
}

.no-transition * {
    transition: none !important;
    animation: none !important;
}

.chart-move {
    transition: transform 0.6s ease;
}

@media only screen and (max-width: 1000px) {
    .total-card {
        padding-left: 11px!important;
    }
}
</style>
